<template>
  <div class="visualization-container">
    <div ref="container" class="threejs-container"></div>
    
    <div class="controls-overlay" v-if="loaded">
      <div class="control-group">
        <v-btn-group>
          <v-btn icon="mdi-rotate-left" @click="resetCamera" variant="text" color="primary" size="small" title="Resetar Câmera"></v-btn>
          <v-btn icon="mdi-refresh" @click="resetSimulation" variant="text" color="primary" size="small" title="Reiniciar Simulação"></v-btn>
          <v-btn icon="mdi-camera" @click="takeScreenshot" variant="text" color="primary" size="small" title="Capturar Imagem"></v-btn>
        </v-btn-group>
      </div>
      
      <div class="legend">
        <div class="legend-item">
          <span class="dot" style="background-color: #4CAF50;"></span>
          <span>Alto Mérito (80-100)</span>
        </div>
        <div class="legend-item">
          <span class="dot" style="background-color: #FFC107;"></span>
          <span>Mérito Médio (50-79)</span>
        </div>
        <div class="legend-item">
          <span class="dot" style="background-color: #F44336;"></span>
          <span>Baixo Mérito (0-49)</span>
        </div>
        <div class="legend-item">
          <span class="dot pulse-animation" style="background-color: #2196F3;"></span>
          <span>Validador Líder</span>
        </div>
        <template v-if="geoCountryLegend.length > 0">
          <div class="legend-divider"></div>
          <div class="legend-item" v-for="entry in geoCountryLegend" :key="entry.country">
            <span class="dot" :style="{ backgroundColor: entry.color }"></span>
            <span>{{ entry.country }}</span>
          </div>
        </template>
      </div>
    </div>
    
    <div v-if="simulationInProgress" class="simulation-overlay">
      <div class="simulation-info">
        <div class="round-indicator">Rodada {{ currentRound }}/{{ totalRounds }}</div>
        <div v-if="currentLeader" class="leader-info">
          <strong>Líder Selecionado:</strong> {{ currentLeader.name }}
          <div class="leader-stats">
            <div>Stake: {{ formatarValorToken(currentLeader.stake) }} {{ coin }}</div>
            <div>Mérito: {{ currentLeader.merit }}/100</div>
            <div>Probabilidade: {{ (currentLeader.probability * 100).toFixed(2) }}%</div>
          </div>
        </div>
        <v-progress-linear
          v-model="simulationProgress"
          color="primary"
          height="8"
          rounded
          class="mt-2"
        ></v-progress-linear>
      </div>
    </div>
    
    <div v-if="!loaded" class="loading-overlay d-flex flex-column align-center justify-center">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <div class="text-body-1 mt-4">Carregando visualização 3D...</div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { formatarValorToken } from '@/services/utilidades'

export default {
  name: 'PoSEM3DVisualization',
  props: {
    validators: {
      type: Array,
      required: true
    }
  },
  emits: ['simulation-complete', 'leader-selected'],
  setup(props, { emit }) {
    // Refs para elementos do DOM e estado
    const container = ref(null)
    const loaded = ref(false)
    const simulationInProgress = ref(false)
    const currentRound = ref(0)
    const totalRounds = ref(5)
    const currentLeader = ref(null)
    const simulationProgress = ref(0)
    const geoCountryLegend = ref([])
    
    // Variáveis para three.js
    let scene, camera, renderer, controls
    let validatorNodes = []
    let animationFrameId = null
    let orbitRadius = 20
    let particleSystem, particleGeometry, particleMaterial
    let countryLegendSprites = []
    
    // Configuração inicial do three.js
    const setupScene = () => {
      // Cria a cena
      scene = new THREE.Scene()
      scene.background = new THREE.Color(0x0a0a0a)
      
      // Adiciona uma luz ambiente
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5)
      scene.add(ambientLight)
      
      // Adiciona uma luz direcional
      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8)
      directionalLight.position.set(0, 10, 10)
      scene.add(directionalLight)
      
      // Configura a câmera
      const width = container.value.clientWidth
      const height = container.value.clientHeight
      camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000)
      camera.position.set(0, 15, 30)
      
      // Configura o renderer
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
      renderer.setSize(width, height)
      renderer.setPixelRatio(window.devicePixelRatio)
      container.value.appendChild(renderer.domElement)
      
      // Adiciona controles de órbita
      controls = new OrbitControls(camera, renderer.domElement)
      controls.enableDamping = true
      controls.dampingFactor = 0.05
      controls.maxDistance = 50
      controls.minDistance = 5
      
      // Adiciona uma grade para referência
      const gridHelper = new THREE.GridHelper(50, 20, 0x303030, 0x303030)
      gridHelper.position.y = -0.01 // Pequeno offset para evitar z-fighting
      scene.add(gridHelper)
      
      // Sistema de partículas para efeitos
      particleGeometry = new THREE.BufferGeometry()
      const particlePositions = new Float32Array(1500 * 3) // 500 partículas * 3 coordenadas
      
      for (let i = 0; i < particlePositions.length; i += 3) {
        const angle = Math.random() * Math.PI * 2
        const radius = Math.random() * 25
        
        particlePositions[i] = Math.cos(angle) * radius // x
        particlePositions[i + 1] = Math.random() * 15 - 7.5 // y
        particlePositions[i + 2] = Math.sin(angle) * radius // z
      }
      
      particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3))
      
      particleMaterial = new THREE.PointsMaterial({
        color: 0x4286f4,
        size: 0.1,
        transparent: true,
        opacity: 0.5
      })
      
      particleSystem = new THREE.Points(particleGeometry, particleMaterial)
      scene.add(particleSystem)
      
      // Adiciona um "piso" para efeito visual
      const planeGeometry = new THREE.PlaneGeometry(50, 50)
      const planeMaterial = new THREE.MeshStandardMaterial({
        color: 0x0f0f0f,
        roughness: 0.8,
        metalness: 0.2
      })
      const plane = new THREE.Mesh(planeGeometry, planeMaterial)
      plane.rotation.x = -Math.PI / 2 // Rota para ficar horizontal
      plane.position.y = -0.1
      scene.add(plane)
      
      // Adiciona a visualização de "energia" central
      const sphereGeometry = new THREE.SphereGeometry(1.5, 32, 32)
      const sphereMaterial = new THREE.MeshStandardMaterial({
        color: 0x2196F3,
        emissive: 0x2196F3,
        emissiveIntensity: 0.5,
        roughness: 0.2,
        metalness: 0.8
      })
      const centreSphere = new THREE.Mesh(sphereGeometry, sphereMaterial)
      scene.add(centreSphere)
      
      // Efeito de "aura" ao redor da esfera central
      const glowGeometry = new THREE.SphereGeometry(1.8, 32, 32)
      const glowMaterial = new THREE.MeshBasicMaterial({
        color: 0x2196F3,
        transparent: true,
        opacity: 0.15
      })
      const glowSphere = new THREE.Mesh(glowGeometry, glowMaterial)
      scene.add(glowSphere)
      
      loaded.value = true
    }
    
    // Country color palette for geo clustering
    const COUNTRY_PALETTE = [
      0x7E57C2, 0x03A9F4, 0xFF4081, 0x4CAF50, 0xFFC107,
      0xFF5252, 0x2196F3, 0xE91E63, 0x9C27B0, 0x00BCD4,
      0xFF9800, 0x795548, 0x607D8B, 0x8BC34A, 0xCDDC39,
    ]
    let countryColors = {}
    let countryColorIndex = 0
    
    const assignColor = (country) => {
      if (!countryColors[country]) {
        countryColors[country] = COUNTRY_PALETTE[countryColorIndex % COUNTRY_PALETTE.length]
        countryColorIndex++
      }
      return countryColors[country]
    }
    
    // Cria representação 3D dos validadores
    const createValidators = () => {
      // Limpa validadores existentes
      validatorNodes.forEach(node => {
        scene.remove(node.mesh)
        if (node.label) scene.remove(node.label)
        if (node.line) scene.remove(node.line)
      })
      validatorNodes = []
      // Reset country colors on recreate
      countryColors = {}
      countryColorIndex = 0
      
      // Remove old country legend sprites
      if (countryLegendSprites) {
        countryLegendSprites.forEach(s => scene.remove(s))
      }
      countryLegendSprites = []
      
      if (!props.validators || props.validators.length === 0) return
      
      // Calcula a probabilidade para cada validador
      const totalWeight = props.validators.reduce((sum, validator) => {
        return sum + calculateWeight(validator)
      }, 0)
      
      // Detect if GeoMetadata is available
      const hasGeo = props.validators.some(v => v.country)
      
      if (hasGeo) {
        // Cluster by country
        const byCountry = {}
        props.validators.forEach(v => {
          const key = v.country || 'unknown'
          if (!byCountry[key]) byCountry[key] = []
          byCountry[key].push(v)
        })
        
        const countries = Object.keys(byCountry)
        const angleStep = (2 * Math.PI) / countries.length
        const clusterRadius = 5
        const clusterOrbitRadius = 15
        
        countries.forEach((country, i) => {
          const cx = Math.cos(i * angleStep) * clusterOrbitRadius
          const cz = Math.sin(i * angleStep) * clusterOrbitRadius
          const members = byCountry[country]
          const innerStep = (2 * Math.PI) / members.length
          const color = assignColor(country)
          
          members.forEach((validator, j) => {
            const weight = calculateWeight(validator)
            const probability = weight / totalWeight
            
            const x = cx + Math.cos(j * innerStep) * clusterRadius
            const z = cz + Math.sin(j * innerStep) * clusterRadius
            const y = 0
            
            const geometry = new THREE.SphereGeometry(0.8 + validator.merit / 100, 32, 32)
            const material = new THREE.MeshStandardMaterial({
              color: color,
              roughness: 0.6,
              metalness: 0.4
            })
            
            const mesh = new THREE.Mesh(geometry, material)
            mesh.position.set(x, y, z)
            scene.add(mesh)
            
            const lineGeometry = new THREE.BufferGeometry().setFromPoints([
              new THREE.Vector3(0, 0, 0),
              new THREE.Vector3(x, y, z)
            ])
            const lineMaterial = new THREE.LineBasicMaterial({
              color: color,
              transparent: true,
              opacity: 0.3
            })
            const line = new THREE.Line(lineGeometry, lineMaterial)
            scene.add(line)
            
            validatorNodes.push({
              validator: { ...validator, probability },
              mesh,
              line,
              position: { x, y, z },
              originalScale: mesh.scale.clone()
            })
          })
        })
        
        // Build country legend
        geoCountryLegend.value = Object.entries(countryColors).map(([country, hex]) => ({
          country,
          color: '#' + hex.toString(16).padStart(6, '0')
        }))
      } else {
        // Fallback: circular layout (original behavior)
        props.validators.forEach((validator, index) => {
          const weight = calculateWeight(validator)
          const probability = weight / totalWeight
          
          const angle = (index / props.validators.length) * Math.PI * 2
          const radius = orbitRadius
          const x = Math.cos(angle) * radius
          const z = Math.sin(angle) * radius
          const y = 0
          
          const geometry = new THREE.SphereGeometry(0.8 + validator.merit / 100, 32, 32)
          const material = new THREE.MeshStandardMaterial({
            color: getMeritColor(validator.merit),
            roughness: 0.6,
            metalness: 0.4
          })
          
          const mesh = new THREE.Mesh(geometry, material)
          mesh.position.set(x, y, z)
          scene.add(mesh)
          
          const lineGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(x, y, z)
          ])
          const lineMaterial = new THREE.LineBasicMaterial({
            color: getMeritColor(validator.merit, 0.3),
            transparent: true,
            opacity: 0.3
          })
          const line = new THREE.Line(lineGeometry, lineMaterial)
          scene.add(line)
          
          validatorNodes.push({
            validator: { ...validator, probability },
            mesh,
            line,
            position: { x, y, z },
            originalScale: mesh.scale.clone()
          })
        })
        
        // No geo data — clear country legend
        geoCountryLegend.value = []
      }
    }
    
    // Anima a cena
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate)
      
      // Atualiza controles de órbita
      if (controls) controls.update()
      
      // Anima partículas para efeito de movimento
      if (particleSystem) {
        particleSystem.rotation.y += 0.001
        
        const positions = particleSystem.geometry.attributes.position.array
        
        for (let i = 0; i < positions.length; i += 3) {
          positions[i + 1] += (Math.random() - 0.5) * 0.02
          
          // Limita a variação de altura
          if (positions[i + 1] > 7.5) positions[i + 1] = 7.5
          if (positions[i + 1] < -7.5) positions[i + 1] = -7.5
        }
        
        particleSystem.geometry.attributes.position.needsUpdate = true
      }
      
      // Anima validadores para efeito de flutuação
      validatorNodes.forEach(node => {
        node.mesh.position.y = Math.sin(Date.now() * 0.001 + node.position.x) * 0.2 + node.position.y
        
        // Atualiza a linha conectando ao centro
        if (node.line) {
          const positions = [
            0, 0, 0,
            node.mesh.position.x, node.mesh.position.y, node.mesh.position.z
          ]
          
          node.line.geometry.setAttribute('position', 
            new THREE.BufferAttribute(new Float32Array(positions), 3)
          )
        }
      })
      
      // Renderiza a cena
      renderer.render(scene, camera)
    }
    
    // Funções auxiliares
    const calculateWeight = (validator) => {
      // Fórmula PoSEM: stake * (1 + fator_mérito * mérito)
      const fatorMerito = 0.01 // 0.01 = máximo de 1x adicional por mérito
      return validator.stake * (1 + fatorMerito * validator.merit)
    }
    
    const getMeritColor = (merit, alpha = 1.0) => {
      // Cores baseadas no mérito
      if (merit >= 80) return new THREE.Color(0x4CAF50).getHex() // Verde
      if (merit >= 50) return new THREE.Color(0xFFC107).getHex() // Amarelo
      return new THREE.Color(0xF44336).getHex() // Vermelho
    }
    
    // Funções de interação
    const resetCamera = () => {
      if (!camera || !controls) return
      
      camera.position.set(0, 15, 30)
      camera.lookAt(0, 0, 0)
      controls.reset()
    }
    
    const resetSimulation = () => {
      if (simulationInProgress.value) {
        endSimulation()
      }
      
      createValidators()
      
      // Reseta os efeitos visuais
      validatorNodes.forEach(node => {
        node.mesh.scale.copy(node.originalScale)
        node.mesh.material.emissive = new THREE.Color(0x000000)
        node.mesh.material.emissiveIntensity = 0
      })
    }
    
    const takeScreenshot = () => {
      renderer.render(scene, camera)
      const dataURL = renderer.domElement.toDataURL('image/png')
      
      // Cria um link para download
      const link = document.createElement('a')
      link.href = dataURL
      link.download = 'posem-visualization.png'
      link.click()
    }
    
    // Simulação da seleção de líder
    const startSimulation = async () => {
      if (simulationInProgress.value || !validatorNodes.length) return
      
      simulationInProgress.value = true
      currentRound.value = 0
      simulationProgress.value = 0
      currentLeader.value = null
      
      // Reseta todos os nós para aparência normal
      validatorNodes.forEach(node => {
        node.mesh.scale.copy(node.originalScale)
        node.mesh.material.emissive = new THREE.Color(0x000000)
        node.mesh.material.emissiveIntensity = 0
      })
      
      // Executar rodadas de seleção
      for (let i = 0; i < totalRounds.value; i++) {
        currentRound.value = i + 1
        
        // Seleciona um líder com base na probabilidade
        await selectLeader()
        
        // Atualiza progresso
        simulationProgress.value = ((i + 1) / totalRounds.value) * 100
        
        // Aguarda um pouco entre rodadas
        if (i < totalRounds.value - 1) {
          await new Promise(resolve => setTimeout(resolve, 3000))
        }
      }
      
      // Finaliza simulação após um delay
      setTimeout(() => {
        emit('simulation-complete')
        simulationInProgress.value = false
      }, 2000)
    }
    
    const selectLeader = async () => {
      return new Promise(resolve => {
        // Gera um número aleatório entre 0 e 1
        const randomValue = Math.random()
        
        // Soma cumulativa das probabilidades
        let cumulativeProbability = 0
        let selectedNode = null
        
        // Seleciona o nó com base na probabilidade
        for (const node of validatorNodes) {
          cumulativeProbability += node.validator.probability
          
          if (randomValue <= cumulativeProbability) {
            selectedNode = node
            break
          }
        }
        
        // Fallback para o último nó se algo der errado
        if (!selectedNode && validatorNodes.length > 0) {
          selectedNode = validatorNodes[validatorNodes.length - 1]
        }
        
        // Destaca o nó selecionado
        if (selectedNode) {
          // Atualiza informações do líder atual
          currentLeader.value = selectedNode.validator
          emit('leader-selected', selectedNode.validator)
          
          // Anima o nó selecionado
          const originalScale = selectedNode.originalScale.clone()
          const targetScale = originalScale.clone().multiplyScalar(1.5)
          
          // Animação de escala (pulso)
          const animate = (timestamp) => {
            const progress = (timestamp % 1000) / 1000 // 0-1 a cada segundo
            const scale = 1 + Math.sin(progress * Math.PI) * 0.2
            
            selectedNode.mesh.scale.copy(originalScale).multiplyScalar(scale)
            selectedNode.mesh.material.emissive = new THREE.Color(0x2196F3)
            selectedNode.mesh.material.emissiveIntensity = 0.5 + Math.sin(progress * Math.PI) * 0.5
            
            if (simulationInProgress.value) {
              requestAnimationFrame(animate)
            }
          }
          
          requestAnimationFrame(animate)
          
          // Conecta o líder ao centro com uma linha mais destacada
          if (selectedNode.line) {
            selectedNode.line.material.color = new THREE.Color(0x2196F3)
            selectedNode.line.material.opacity = 0.8
          }
          
          // Emite um raio do centro até o líder
          const particleCount = 50
          const beamGeometry = new THREE.BufferGeometry()
          const positions = new Float32Array(particleCount * 3)
          
          const updateBeam = () => {
            for (let i = 0; i < particleCount; i++) {
              const t = i / particleCount
              
              positions[i * 3] = selectedNode.mesh.position.x * t
              positions[i * 3 + 1] = selectedNode.mesh.position.y * t
              positions[i * 3 + 2] = selectedNode.mesh.position.z * t
            }
            
            beamGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
          }
          
          updateBeam()
          
          const beamMaterial = new THREE.PointsMaterial({
            color: 0x2196F3,
            size: 0.3,
            transparent: true,
            opacity: 0.8,
            blending: THREE.AdditiveBlending
          })
          
          const beam = new THREE.Points(beamGeometry, beamMaterial)
          scene.add(beam)
          
          // Anima o raio
          let beamAnimationFrameId
          const animateBeam = () => {
            beamMaterial.size = 0.3 + Math.sin(Date.now() * 0.005) * 0.2
            beamMaterial.opacity = 0.6 + Math.sin(Date.now() * 0.005) * 0.4
            
            beamAnimationFrameId = requestAnimationFrame(animateBeam)
          }
          
          animateBeam()
          
          // Remove o raio após alguns segundos
          setTimeout(() => {
            cancelAnimationFrame(beamAnimationFrameId)
            scene.remove(beam)
            resolve()
          }, 2500)
        } else {
          resolve()
        }
      })
    }
    
    const endSimulation = () => {
      simulationInProgress.value = false
      currentLeader.value = null
    }
    
    // Função para redimensionar a visualização quando a janela muda de tamanho
    const handleResize = () => {
      if (!camera || !renderer || !container.value) return
      
      const width = container.value.clientWidth
      const height = container.value.clientHeight
      
      camera.aspect = width / height
      camera.updateProjectionMatrix()
      
      renderer.setSize(width, height)
    }
    
    // Observa mudanças na lista de validadores
    watch(() => props.validators, () => {
      if (scene) createValidators()
    }, { deep: true })
    
    // Configuração inicial quando o componente é montado
    onMounted(() => {
      if (container.value) {
        setupScene()
        createValidators()
        animate()
        
        window.addEventListener('resize', handleResize)
      }
    })
    
    // Limpa recursos quando o componente é desmontado
    onBeforeUnmount(() => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
      
      window.removeEventListener('resize', handleResize)
      
      if (renderer) {
        renderer.dispose()
        if (container.value && renderer.domElement) {
          container.value.removeChild(renderer.domElement)
        }
      }
      
      // Limpa geometrias e materiais
      validatorNodes.forEach(node => {
        if (node.mesh) {
          node.mesh.geometry.dispose()
          node.mesh.material.dispose()
        }
        if (node.line) {
          node.line.geometry.dispose()
          node.line.material.dispose()
        }
      })
      
      if (particleGeometry) particleGeometry.dispose()
      if (particleMaterial) particleMaterial.dispose()
      
      countryLegendSprites.forEach(s => {
        if (s.geometry) s.geometry.dispose()
        if (s.material) s.material.dispose()
      })
    })
    
    return {
      container,
      loaded,
      simulationInProgress,
      currentRound,
      totalRounds,
      currentLeader,
      simulationProgress,
      geoCountryLegend,
      resetCamera,
      resetSimulation,
      takeScreenshot,
      startSimulation,
      endSimulation,
      formatarValorToken
    }
  }
}
</script>

<style scoped>
.visualization-container {
  position: relative;
  width: 100%;
  height: 80vh;
  max-height: 800px;
  overflow: hidden;
  border-radius: 8px;
  background-color: #0a0a0a;
}

.threejs-container {
  width: 100%;
  height: 100%;
}

.controls-overlay {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
}

.control-group {
  background-color: rgba(10, 10, 10, 0.6);
  border-radius: 4px;
  padding: 4px;
  margin-bottom: 8px;
}

.legend {
  background-color: rgba(10, 10, 10, 0.6);
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.legend-divider {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  margin: 6px 0;
}

.dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
}

.simulation-overlay {
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  z-index: 10;
}

.simulation-info {
  background-color: rgba(10, 10, 10, 0.8);
  border-radius: 8px;
  padding: 16px;
  max-width: 400px;
  margin: 0 auto;
}

.round-indicator {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: center;
}

.leader-info {
  margin-bottom: 12px;
}

.leader-stats {
  margin-top: 4px;
  display: flex;
  gap: 12px;
  font-size: 14px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(10, 10, 10, 0.8);
  z-index: 20;
}

.pulse-animation {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 8px rgba(33, 150, 243, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
}
</style>
